"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Comparison() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="newvsusedcomparison" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-12">
          {/* Header block */}
          <motion.div variants={itemVariants} className="max-w-3xl">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground leading-tight">
              New vs Used Inflatable Mechanical Bulls
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              New and used inflatable mechanical bulls should be compared differently because a used package has two major condition areas to evaluate: the mechanical equipment and the inflatable arena.
            </p>
          </motion.div>

          {/* Two-Column Comparison Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Left Column: New */}
            <motion.div variants={itemVariants} className="bg-card text-card-foreground border border-border flex flex-col justify-between p-6 sm:p-8 relative">
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-5 text-primary shrink-0" />
                    <h3 className="font-mono text-sm sm:text-base uppercase tracking-wider font-semibold text-foreground">
                      New Inflatable Mechanical Bull
                    </h3>
                  </div>
                </div>

                <p className="text-foreground/90 text-sm sm:text-base leading-relaxed">
                  A new system provides a known starting condition. Still confirm the exact controller, inflatable, blower, accessories, product documentation and whatever warranty or support genuinely applies to that product.
                </p>
              </div>

              <div className="pt-8 mt-6 border-t border-border">
                <Button asChild variant="default" className="w-full sm:w-auto font-mono uppercase tracking-wider text-xs font-semibold px-6 py-2.5">
                  <Link to="/NewMechanicalBullsPage" className="inline-flex items-center justify-center gap-2">
                    VIEW NEW BULLS
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Right Column: Used */}
            <motion.div variants={itemVariants} className="bg-card text-card-foreground border border-border flex flex-col justify-between p-6 sm:p-8 relative">
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="size-5 text-chart-1 shrink-0" />
                    <h3 className="font-mono text-sm sm:text-base uppercase tracking-wider font-semibold text-foreground">
                      Used Inflatable Mechanical Bull
                    </h3>
                  </div>
                  <span className="font-mono text-xs px-2 py-0.5 bg-muted text-muted-foreground uppercase">
                    Pre-Owned
                  </span>
                </div>

                <p className="text-foreground/90 text-sm sm:text-base leading-relaxed">
                  A used system can reduce the initial purchase cost, but review the mechanical equipment and inflatable separately. Check the available condition information, known repairs, controller, visible inflatable wear and included equipment.
                </p>
              </div>

              <div className="pt-8 mt-6 border-t border-border">
                <Button asChild variant="outline" className="w-full sm:w-auto font-mono uppercase tracking-wider text-xs font-semibold px-6 py-2.5">
                  <Link to="/UsedMechanicalBullsPage" className="inline-flex items-center justify-center gap-2">
                    VIEW USED BULLS
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Closing callout note */}
          <motion.div variants={itemVariants} className="border-t border-b border-border py-4 px-5 bg-muted/40">
            <p className="font-mono text-xs sm:text-sm text-foreground/90 leading-normal">
              <strong className="uppercase tracking-wider text-primary">Notice:</strong> A lower-priced used package is only useful if its condition and included equipment make sense for your needs.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}